import { Trans } from "@lingui/macro";
import Image, { StaticImageData } from "next/image";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Stack } from "~/guidelines/Flex";
import { subtitle2 } from "~/guidelines/Typography";
import Arrows from "../_assets/our-projects/arrows.svg";

export interface ProjectData {
  id: string;
  label: string;
  beforeSrc: StaticImageData | string;
  afterSrc: StaticImageData | string;
}

const ImageComparerWrapper = styled.div`
  border: 3px solid black;
  position: relative;
  width: 100%;
  max-height: 70vh;
  max-width: 1600px;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 24px;
`;

const ImageWrapperBase = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  img {
    object-fit: cover;
    object-position: center;
  }
`;

const BeforeImageWrapper = styled(ImageWrapperBase)``;
const AfterImageWrapper = styled(ImageWrapperBase)``;

const SliderInput = styled.input.attrs({ type: "range" })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: ew-resize;
  opacity: 0;
  z-index: 20;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 40px;
    height: 100%;
    cursor: ew-resize;
  }
  &::-moz-range-thumb {
    width: 40px;
    height: 100%;
    border: none;
    background: transparent;
    cursor: ew-resize;
  }
`;

const SliderVisuals = styled.div`
  background-color: black;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  pointer-events: none;
  z-index: 15;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SliderHandle = styled.div`
  width: 54px;
  height: 54px;
  min-width: 54px;
  min-height: 54px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.background.darkTertiary};
  border: 3px solid black;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
`;

const ControlsBanner = styled(Stack)`
  position: relative;
  bottom: 70px;
  z-index: 20;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  background-color: #fff;
  border-radius: 28px;
  padding: 2px;
  width: fit-content;
  margin: 0 auto;
`;

const ControlButton = styled.button<{ isActive: boolean }>`
  ${subtitle2};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-transform: uppercase;
  border-radius: 28px;
  border: ${({ isActive }) =>
    isActive ? "1px solid black" : "1px solid transparent"};
  padding: 10px 16px;
  background-color: #fff;
`;

const InfoTag = styled.span`
  position: absolute;
  top: 20px;
  display: inline-flex;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 60px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12.6px);
  color: #fff;
  font-size: 11px;
  font-family: "Public Sans", sans-serif;
`;

const InfoTagLeft = styled(InfoTag)`
  left: 20px;
`;

const InfoTagRight = styled(InfoTag)`
  right: 20px;
`;

interface OurProjectsDesktopProps {
  projects: ProjectData[];
}

const OurProjectsDesktop: React.FC<OurProjectsDesktopProps> = ({
  projects,
}) => {
  const [desktopActiveIndex, setDesktopActiveIndex] = useState(0);
  const [desktopSliderPosition, setDesktopSliderPosition] = useState(50);

  const desktopAfterImageRef = useRef<HTMLDivElement>(null);
  const desktopSliderVisualsRef = useRef<HTMLDivElement>(null);

  const handleDesktopSliderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(event.target.value);
    setDesktopSliderPosition(value);
  };

  useEffect(() => {
    if (desktopAfterImageRef.current)
      desktopAfterImageRef.current.style.clipPath = `inset(0 ${100 - desktopSliderPosition}% 0 0)`;

    if (desktopSliderVisualsRef.current)
      desktopSliderVisualsRef.current.style.left = `${desktopSliderPosition}%`;
  }, [desktopSliderPosition]);

  useEffect(() => {
    setDesktopSliderPosition(50);
  }, [desktopActiveIndex]);

  const activeProject = projects[desktopActiveIndex];

  if (!activeProject) {
    return null;
  }

  return (
    <div>
      <ImageComparerWrapper>
        <AfterImageWrapper>
          <Image
            src={activeProject.afterSrc}
            alt={`After renovation - ${activeProject.label}`}
            fill
            priority={desktopActiveIndex === 0}
          />
          <InfoTagRight>
            <Trans>After renovation</Trans>
          </InfoTagRight>
        </AfterImageWrapper>

        <BeforeImageWrapper ref={desktopAfterImageRef}>
          <Image
            src={activeProject.beforeSrc}
            alt={`Before renovation - ${activeProject.label}`}
            fill
            priority={desktopActiveIndex === 0}
          />
          <InfoTagLeft>
            <Trans>Before renovation</Trans>
          </InfoTagLeft>
        </BeforeImageWrapper>

        <SliderVisuals ref={desktopSliderVisualsRef}>
          <SliderHandle>
            <Image src={Arrows} style={{ margin: "auto" }} alt="arrows" />
          </SliderHandle>
        </SliderVisuals>

        <SliderInput
          min="0"
          max="100"
          value={desktopSliderPosition}
          onChange={handleDesktopSliderChange}
          aria-label="Image comparison slider"
        />
      </ImageComparerWrapper>

      <ControlsBanner>
        {projects.map((project, index) => (
          <ControlButton
            key={project.id}
            isActive={index === desktopActiveIndex}
            onClick={() => setDesktopActiveIndex(index)}
          >
            {project.label}
          </ControlButton>
        ))}
      </ControlsBanner>
    </div>
  );
};

export default OurProjectsDesktop;
